import { useState } from 'react'

import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useMemo } from 'modules/core/hooks/useMemo'
import { stringToDate } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import { useAddCustomShifts } from 'modules/shift_management/hooks/useAddCustomShifts'
import { ShiftsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import {
  onAddPlannedBreak,
  onDeletePlannedBreak,
} from 'modules/shift_management/lib/managePlannedBreaks'
import { Leave } from 'modules/shift_management/lib/types'

interface BuildParams {
  defaultAttributes: ShiftsAttributesByDate
  daysByRow: Record<string, string[]>
}

interface SubmitParams extends BuildParams {
  weekDays: string
  from: 'modal' | 'side_panel' | 'quick_actions'
  advancedOptions?: boolean
}

const useCustomShiftForm = ({
  daysByEmployee,
  isBackup,
  leave,
  contractVersions,
}: {
  daysByEmployee: DaysByRow
  isBackup?: boolean
  leave?: Leave
  contractVersions: ContractVersions
}) => {
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()
  const [plannedBreakForms, setPlannedBreakForms] = useState<PlannedBreakForm[]>([])

  const selectedTimes = Object.values(daysByEmployee)
    .flat()
    .map((date) => stringToDate(date, view.dateFormat))
  const hasDefaultTimes = view.isDaily && hasSelection
  const startTime = hasDefaultTimes ? moment.min(selectedTimes).format('HH:mm') : ''
  const endTime = hasDefaultTimes ? moment.max(selectedTimes).add('1', 'h').format('HH:mm') : ''

  const customShiftForm = useMemo(
    () =>
      new ShiftForm({
        start_time: startTime,
        end_time: endTime,
      }),
    [startTime, endTime]
  )

  const onAddBreak = (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => {
    onAddPlannedBreak(breakConfiguration, breakType, plannedBreakForms, (value) => {
      setPlannedBreakForms(value)
    })
  }

  const onDeleteBreak = (breakForm: PlannedBreakForm) => {
    onDeletePlannedBreak(breakForm, plannedBreakForms, setPlannedBreakForms)
  }

  const useSubmitCustomShift = ({
    defaultAttributes,
    weekDays,
    daysByRow,
    from,
    advancedOptions = false,
  }: SubmitParams) => {
    const { onSubmit, submitting } = useAddCustomShifts({
      form: customShiftForm,
      weekDays,
      defaultAttributes,
      daysByRow,
      from,
      advancedOptions,
      isBackup,
      leave,
      onSuccess: () => {
        dispatchSelection({
          type: 'CLEAR_SELECTION',
        })
      },
      plannedBreakForms,
      contractVersions,
    })

    return {
      onSubmit,
      submitting,
    }
  }

  return {
    customShiftForm,
    useSubmitCustomShift,
    onAddBreak,
    onDeleteBreak,
    plannedBreakForms,
  }
}

export default useCustomShiftForm
